import {
  DeploymentConfig,
  LoadDeploymentConfig,
  loadDeploymentConfigFromOldOrNewPath,
} from "./deploymentconfig"
import { FirebaseApp } from "firebase/app"
import { Auth, getAuth } from "firebase/auth"
import { Firestore, getFirestore } from "firebase/firestore"
import { FirebaseStorage, getStorage } from "firebase/storage"
import { getOrCreateFirebaseAppWithEmulators } from "./firebase"

export type MyCAppAppOptions = {
  deploymentConfig: DeploymentConfig
  firebase: FirebaseApp
  firestore?: Firestore
  auth?: Auth
  storage?: FirebaseStorage
}
/** Global application state/configuration (not tied to a specific user). */
export class MyCAppApp {
  /** MyCApp's settings for the deployment environment (dev/integration/prod etc). */
  readonly deploymentConfig: DeploymentConfig
  /** The firebase app MyCApp is using in the current deployment environment. */
  readonly firebase: FirebaseApp
  /** The firestore (database) client for MyCApp's firebase app. */
  readonly firestore: Firestore
  /** The auth client for MyCApp's firebase app. */
  readonly auth: Auth
  /** The storage client for MyCApp's firebase app. */
  readonly storage: FirebaseStorage

  constructor({
    deploymentConfig,
    firebase,
    firestore,
    auth,
    storage,
  }: MyCAppAppOptions) {
    this.deploymentConfig = deploymentConfig
    this.firebase = firebase
    this.firestore = firestore ?? getFirestore(firebase)
    this.auth = auth ?? getAuth(firebase)
    this.storage = storage ?? getStorage(firebase)
  }

  static fromDeploymentConfig(deploymentConfig: DeploymentConfig): MyCAppApp {
    return new MyCAppApp({
      deploymentConfig,
      firebase: getOrCreateFirebaseAppWithEmulators(
        deploymentConfig.firebaseConfig,
      ).value,
    })
  }

  static async fromLoadedDeploymentConfig(options: {
    loadDeploymentConfig?: LoadDeploymentConfig
    signal?: AbortSignal
  }): Promise<MyCAppApp> {
    const {
      loadDeploymentConfig = loadDeploymentConfigFromOldOrNewPath,
      signal,
    } = options

    const loadResult = await loadDeploymentConfig({ signal })
    if (!loadResult.success) {
      const { cause, reason } = loadResult.error
      throw new Error(`Failed to load deployment config: ${reason}`, { cause })
    }
    return MyCAppApp.fromDeploymentConfig(loadResult.data)
  }
}
